import type { CourseModule } from '@/types/lesson';

/**
 * Модуль 1: Основы Git
 */
const module1: CourseModule = {
  id: 'module-1',
  title: 'Основы Git',
  slug: 'git-fundamentals',
  order: 1,
  lessons: [
    {
      id: '1-1',
      moduleId: 'module-1',
      title: 'Что такое Git?',
      slug: 'what-is-git',
      order: 1,
      theory: `# Что такое Git?

Git — это **распределённая система контроля версий**, которая отслеживает изменения в исходном коде во время разработки программного обеспечения.

## История создания Git

Git был создан **Линусом Торвальдсом** в 2005 году для разработки ядра Linux.

### Предпосылки создания:
- В 2005 году BitKeeper (коммерческая VCS, использовавшаяся для Linux) отозвал бесплатную лицензию
- Линусу нужен был инструмент для координации работы тысяч разработчиков
- Требования: скорость, надёжность, поддержка нелинейной разработки

### Эволюция систем контроля версий:
1. **Локальные VCS** (1980-е): RCS — только локальные файлы
2. **Централизованные VCS** (1990-е): CVS, Subversion — один центральный сервер
3. **Распределённые VCS** (2000-е): Git, Mercurial — каждый имеет полную копию

### Почему Git победил:
- **Скорость**: Операции выполняются локально без сети
- **Ветвление**: Создание веток занимает миллисекунды
- **Целостность**: SHA-1 хеши гарантируют неизменность данных
- **Гибкость**: Подходит для любых рабочих процессов

## Зачем нужен Git?

### Для индивидуального разработчика:
- **История изменений**: Каждое изменение записывается. Вы можете вернуться в любой момент времени.
- **Эксперименты без риска**: Ветки позволяют пробовать новые подходы
- **Документация**: История коммитов показывает эволюцию проекта

### Для команды:
- **Совместная работа**: Несколько разработчиков могут работать над одним проектом параллельно
- **Code Review**: Pull Requests для проверки кода
- **Разрешение конфликтов**: Инструменты для слияния изменений

### Для проекта:
- **Резервное копирование**: Ваш код хранится в нескольких местах
- **CI/CD интеграция**: Автоматические тесты и деплой
- **Откат изменений**: Возможность вернуть любое предыдущее состояние

## Ключевые понятия

### Репозиторий
Репозиторий (или "репо") — это директория, которая содержит файлы вашего проекта и историю Git.

**Внутреннее устройство:**
\`\`\`
.git/
├── HEAD          # Указатель на текущую ветку
├── config        # Локальная конфигурация
├── objects/      # База данных всех объектов (commits, trees, blobs)
├── refs/         # Указатели на коммиты (branches, tags)
├── index         # Состояние staging area
└── logs/         # История изменений reflog
\`\`\`

### Коммит
Коммит — это снимок (snapshot) вашего проекта в определённый момент времени.

**Структура коммита:**
- **SHA-1 хеш**: Уникальный идентификатор (например, a1b2c3d...)
- **Автор**: Имя и email
- **Дата**: Временная метка
- **Сообщение**: Описание изменений
- **Родитель(и)**: Ссылки на предыдущие коммиты
- **Tree**: Ссылка на дерево файлов

### Ветка
Ветка — это параллельная версия вашего репозитория, где вы можете вносить изменения, не затрагивая основной код.

**Технически ветка — это:**
- Файл в \`.git/refs/heads/\` с SHA-1 хешем коммита
- Легковесный указатель (создаётся за миллисекунды)
- По умолчанию Git создаёт ветку \`main\` (или \`master\`)

## Три дерева Git

Git управляет тремя "деревьями", которые представляют различные состояния вашего проекта:

1. **Рабочая директория (Working Directory)** — Ваши фактические файлы на диске
2. **Индекс (Staging Area)** — Изменения, готовые к коммиту
3. **Репозиторий (.git директория)** — Все ваши коммиты и история

Понимание этих трёх деревьев фундаментально для освоения Git.

## Глубокое погружение: Как Git хранит данные

### Объекты Git:
1. **Blob** (binary large object) — содержимое файла
2. **Tree** — директория (список файлов и поддиректорий)
3. **Commit** — снимок + метаданные
4. **Tag** — именованная ссылка на коммит

### Пример хранения:
\`\`\`
Commit: a1b2c3d "Добавить README"
  │
  └── Tree: t1e2s3t (корневая директория)
        ├── Blob: r4e5a6d (README.md)
        └── Tree: s7r8c9 (src/)
              └── Blob: i0n1d2x (index.ts)
\`\`\`

Каждый объект идентифицируется SHA-1 хешем его содержимого. Это гарантирует:
- **Целостность**: Любое изменение даёт новый хеш
- **Дедупликацию**: Одинаковые файлы хранятся один раз
- **Неизменность**: Объекты нельзя изменить, только создать новые

## Практические кейсы

### Кейс 1: "Я случайно удалил файл"
\`\`\`bash
# Восстановление из последнего коммита
git checkout HEAD -- deleted-file.txt

# Или из конкретного коммита
git checkout a1b2c3d -- deleted-file.txt
\`\`\`

### Кейс 2: "Мне нужно посмотреть, кто изменил эту строку"
\`\`\`bash
# Показать, кто и когда изменил каждую строку
git blame filename.txt

# Показать историю изменений конкретной строки
git log -L 10,20:filename.txt
\`\`\`

### Кейс 3: "Я хочу понять, как проект развивался"
\`\`\`bash
# История с визуализацией веток
git log --oneline --graph --all

# Статистика по авторам
git shortlog -sn
\`\`\`
`,
      steps: [
        {
          id: 1,
          title: 'Инициализация Git репозитория',
          description:
            'Создайте новый Git репозиторий в текущей директории с помощью команды git init.',
          expectedCommand: 'git init',
          hint: {
            level1:
              'Вам нужно инициализировать новый Git репозиторий. Подумайте, какая команда создаёт новый репо.',
            level2: 'Используйте команду git init для создания нового репозитория.',
            level3: 'Введите: git init',
          },
        },
      ],
      keyInsight: {
        main: 'Git init создаёт скрытую директорию .git, которая содержит всю информацию о контроле версий.',
        example:
          'Когда вы выполняете git init в папке проекта, Git создаёт структуру для хранения всех коммитов, веток и настроек. Это как создание "скелета" для истории вашего проекта.',
        warning:
          'Не удаляйте директорию .git — это приведёт к полной потере истории проекта! Если нужно удалить Git из проекта, просто удалите эту директорию.',
        tip: 'Используйте git init --bare для создания "голого" репозитория без рабочей директории — это полезно для серверных репозиториев.',
        additional: [
          'Инициализация не создаёт ни одного коммита — репозиторий пуст до первого коммита.',
          'По умолчанию создаётся ветка с именем "main" (или "master" в старых версиях Git).',
        ],
      },
      estimatedTime: 10,
      quizQuestions: [
        {
          id: '1-1-q1',
          question: 'Кто создал Git и в каком году?',
          options: [
            {
              id: 'a',
              text: 'Линус Торвальдс, 2005',
              isCorrect: true,
              explanation:
                'Правильно! Линус Торвальдс создал Git в 2005 году для разработки ядра Linux после того, как BitKeeper отозвал бесплатную лицензию.',
            },
            {
              id: 'b',
              text: 'Марк Цукерберг, 2004',
              isCorrect: false,
              explanation: 'Неправильно. Марк Цукерберг создал Facebook, а не Git.',
            },
            {
              id: 'c',
              text: 'Билл Гейтс, 2000',
              isCorrect: false,
              explanation:
                'Неправильно. Билл Гейтс — основатель Microsoft, не связан с созданием Git.',
            },
            {
              id: 'd',
              text: 'Кен Томпсон, 1991',
              isCorrect: false,
              explanation: 'Неправильно. Кен Томпсон — один из создателей Unix и Go, но не Git.',
            },
          ],
          explanation:
            'Git был создан Линусом Торвальдсом в 2005 году для координации работы над ядром Linux. Это была реакция на отзыв бесплатной лицензии BitKeeper.',
          difficulty: 'easy',
        },
        {
          id: '1-1-q2',
          question: 'Какой тип системы контроля версий представляет Git?',
          options: [
            {
              id: 'a',
              text: 'Локальная VCS',
              isCorrect: false,
              explanation:
                'Неправильно. Локальные VCS (например, RCS) хранят версии только на одном компьютере.',
            },
            {
              id: 'b',
              text: 'Централизованная VCS',
              isCorrect: false,
              explanation:
                'Неправильно. Централизованные VCS (CVS, Subversion) имеют один центральный сервер.',
            },
            {
              id: 'c',
              text: 'Распределённая VCS',
              isCorrect: true,
              explanation:
                'Правильно! Git — распределённая система, где каждый разработчик имеет полную копию репозитория.',
            },
            {
              id: 'd',
              text: 'Облачная VCS',
              isCorrect: false,
              explanation: 'Неправильно. Это не стандартная классификация VCS.',
            },
          ],
          explanation:
            'Git — распределённая система контроля версий (DVCS). Каждый разработчик имеет полную копию репозитория со всей историей, что позволяет работать офлайн и обеспечивает надёжность.',
          difficulty: 'easy',
        },
        {
          id: '1-1-q3',
          question: 'Какие объекты хранятся в директории .git/objects/?',
          options: [
            {
              id: 'a',
              text: 'Только исходный код',
              isCorrect: false,
              explanation:
                'Неправильно. В .git/objects хранятся не только файлы, но и другие объекты.',
            },
            {
              id: 'b',
              text: 'Blobs, Trees, Commits, Tags',
              isCorrect: true,
              explanation:
                'Правильно! Git хранит четыре типа объектов: blob (содержимое файла), tree (директория), commit (снимок), tag (метка).',
            },
            {
              id: 'c',
              text: 'Только конфигурационные файлы',
              isCorrect: false,
              explanation: 'Неправильно. Конфигурация хранится в .git/config, а не в objects.',
            },
            {
              id: 'd',
              text: 'Только логи операций',
              isCorrect: false,
              explanation: 'Неправильно. Логи хранятся в .git/logs/, а не в objects.',
            },
          ],
          explanation:
            'В .git/objects/ хранятся все объекты Git: blob (содержимое файлов), tree (структура директорий), commit (коммиты с метаданными) и tag (теги).',
          difficulty: 'medium',
        },
        {
          id: '1-1-q4',
          question: 'Что гарантирует использование SHA-1 хешей в Git?',
          options: [
            {
              id: 'a',
              text: 'Скорость операций',
              isCorrect: false,
              explanation:
                'Неправильно. SHA-1 не связан со скоростью, он обеспечивает целостность.',
            },
            {
              id: 'b',
              text: 'Целостность и неизменность данных',
              isCorrect: true,
              explanation:
                'Правильно! SHA-1 хеш гарантирует, что любое изменение содержимого приведёт к новому хешу, обеспечивая целостность.',
            },
            {
              id: 'c',
              text: 'Сжатие файлов',
              isCorrect: false,
              explanation: 'Неправильно. Сжатие обеспечивается другими механизмами Git.',
            },
            {
              id: 'd',
              text: 'Шифрование данных',
              isCorrect: false,
              explanation: 'Неправильно. SHA-1 — это хеш-функция, а не шифрование.',
            },
          ],
          explanation:
            'SHA-1 хеши в Git гарантируют целостность данных: любое изменение содержимого объекта приведёт к изменению его хеша. Это делает невозможным незаметное изменение данных.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git init',
          url: 'https://git-scm.com/docs/git-init',
          type: 'documentation',
          description: 'Официальная документация Git о команде init',
        },
        {
          title: 'Pro Git Book: Getting Started',
          url: 'https://git-scm.com/book/en/v2/Getting-Started-About-Version-Control',
          type: 'book',
          description: 'Первая глава книги Pro Git о системах контроля версий',
        },
        {
          title: 'Git Basics for Beginners',
          url: 'https://www.youtube.com/watch?v=HVsySz-h9r4',
          type: 'video',
          description: 'Видео-введение в Git от Corey Schafer',
        },
      ],
    },
    {
      id: '1-2',
      moduleId: 'module-1',
      title: 'Инициализация репозитория',
      slug: 'repository-initialization',
      order: 2,
      theory: `# Инициализация репозитория

Когда вы выполняете \`git init\`, Git создаёт скрытую директорию \`.git\`, которая содержит все метаданные и информацию о контроле версий.

## Что происходит при инициализации?

1. Создаётся директория \`.git\`
2. Настраивается конфигурация по умолчанию
3. Создаётся начальная ветка (обычно 'main' или 'master')
4. Подготавливается индекс (staging area)

## Глубокое погружение: Структура .git директории

После \`git init\` создаётся следующая структура:

\`\`\`
.git/
├── HEAD           # Указатель на текущую ветку (ref: refs/heads/main)
├── config         # Локальная конфигурация репозитория
├── description    # Описание репозитория (для GitWeb)
├── hooks/         # Скрипты-хуки для автоматизации
│   ├── pre-commit.sample
│   ├── pre-push.sample
│   └── ...
├── info/          # Дополнительная информация
│   └── exclude    # Локальный .gitignore
├── objects/       # База данных объектов Git
│   ├── info/
│   └── pack/
└── refs/          # Ссылки на коммиты
    ├── heads/     # Локальные ветки
    └── tags/      # Теги
\`\`\`

### Файл HEAD
\`\`\`
ref: refs/heads/main
\`\`\`
Это указатель на текущую ветку. При создании первого коммита Git создаст файл \`refs/heads/main\` с хешем этого коммита.

### Файл config
\`\`\`ini
[core]
    repositoryformatversion = 0
    filemode = true
    bare = false
    logallrefupdates = true
\`\`\`

Здесь хранятся настройки репозитория: игнорируемые файлы, remote-адреса, настройки слияния.

## Проверка состояния репозитория

Используйте \`git status\`, чтобы увидеть:
- Текущую ветку
- Изменения в индексе
- Изменения не в индексе
- Неотслеживаемые файлы

### Подробный анализ git status

\`\`\`bash
$ git status
On branch main                    # Текущая ветка

No commits yet                    # История пуста

Untracked files:                  # Файлы, о которых Git не знает
  (use "git add <file>..." to include in what will be committed)
    README.md
    src/

nothing added to commit but untracked files present (use "git add" to track)
\`\`\`

### Коды статуса файлов

При использовании \`git status --short\`:

| Код | Значение |
|-----|----------|
| \`??\` | Неотслеживаемый файл |
| \`A \` | Добавлен в индекс (новый файл) |
| \`M \` | Изменён и добавлен в индекс |
| \` M\` | Изменён, но НЕ в индексе |
| \`MM\` | Изменён, частично в индексе |
| \`D \` | Удалён из индекса |
| \` D\` | Удалён из рабочей директории |
| \`R \` | Переименован в индексе |
| \`C \` | Скопирован в индексе |

## Пустой vs Инициализированный репозиторий

Инициализированный репозиторий начинается пустым — коммитов ещё нет. Вам нужно:
1. Создать файлы
2. Добавить их в индекс с помощью \`git add\`
3. Закоммитить с помощью \`git commit\`

### Bare-репозитории

\`\`\`bash
# Создать "голый" репозиторий (без рабочей директории)
git init --bare project.git
\`\`\`

**Bare-репозиторий** используется для:
- Серверных репозиториев (GitHub, GitLab, Bitbucket)
- Центральных хранилищ для команды
- CI/CD систем

Отличия от обычного репозитория:
- Нет рабочей директории (только .git содержимое)
- Нельзя коммитить напрямую
- Предназначен только для push/pull

## Практические кейсы

### Кейс 1: "Хочу инициализировать Git в существующем проекте"
\`\`\`bash
cd existing-project/
git init
git add .
git commit -m "Initial commit: добавляем существующий проект в Git"
\`\`\`

### Кейс 2: "Нужно создать репозиторий на сервере"
\`\`\`bash
# На сервере
mkdir project.git
cd project.git
git init --bare

# На локальной машине
git remote add origin user@server:/path/to/project.git
git push -u origin main
\`\`\`

### Кейс 3: "Хочу понять, что находится в .git"
\`\`\`bash
# Посмотреть структуру
find .git -type f

# Посмотреть содержимое объекта
git cat-file -p HEAD

# Посмотреть тип объекта
git cat-file -t HEAD
\`\`\`

## Типичные ошибки

### Ошибка: "Not a git repository"
\`\`\`bash
fatal: not a git repository (or any of the parent directories): .git
\`\`\`

**Причина**: Вы находитесь в директории, где нет .git
**Решение**: Выполните \`git init\` или перейдите в правильную директорию

### Ошибка: "Reinitialized existing Git repository"
\`\`\`bash
Reinitialized existing Git repository in /path/to/project/.git/
\`\`\`

**Причина**: \`git init\` выполнен в уже инициализированном репозитории
**Решение**: Это предупреждение, не ошибка. Git просто обновляет структуру.

## Связанные команды

\`\`\`bash
# Клонировать существующий репозиторий
git clone <url>

# Проверить, это Git-репозиторий?
git rev-parse --is-inside-work-tree

# Показать корень репозитория
git rev-parse --show-toplevel

# Показать .git директорию
git rev-parse --git-dir
\`\`\`
`,
      steps: [
        {
          id: 1,
          title: 'Проверка состояния репозитория',
          description: 'Используйте git status, чтобы увидеть текущее состояние репозитория.',
          expectedCommand: 'git status',
          hint: {
            level1: 'Вы хотите увидеть, какие файлы отслеживаются и какие изменения существуют.',
            level2: 'Используйте команду git status.',
            level3: 'Введите: git status',
          },
        },
      ],
      keyInsight: {
        main: 'Git status — ваш лучший друг. Используйте его часто, чтобы понимать текущее состояние репозитория.',
        example:
          'Перед каждой операцией с Git делайте привычку выполнять git status. Это покажет: какие файлы изменены, какие в индексе, на какой ветке вы находитесь. Например, перед коммитом status покажет, что именно будет закоммичено.',
        warning:
          'Не игнорируйте неотслеживаемые файлы в выводе status — они могут содержать важные файлы конфигурации или секреты, которые случайно можно закоммитить.',
        tip: 'Используйте git status -s для компактного вывода, когда нужно быстро проверить состояние. Также полезен алиас git st для экономии времени.',
        additional: [
          'git status показывает разницу между рабочей директорией, индексом и последним коммитом.',
          'Флаг --porcelain даёт машиночитаемый вывод для скриптов.',
          'Зелёные файлы — в индексе, красные — изменены, но не добавлены.',
        ],
      },
      estimatedTime: 10,
      quizQuestions: [
        {
          id: '1-2-q1',
          question: 'Какой файл в директории .git указывает на текущую ветку?',
          options: [
            {
              id: 'a',
              text: 'config',
              isCorrect: false,
              explanation: 'Неправильно. config содержит настройки репозитория.',
            },
            {
              id: 'b',
              text: 'HEAD',
              isCorrect: true,
              explanation:
                'Правильно! HEAD — это указатель на текущую ветку (например, ref: refs/heads/main).',
            },
            {
              id: 'c',
              text: 'index',
              isCorrect: false,
              explanation: 'Неправильно. index — это бинарный файл staging area.',
            },
            {
              id: 'd',
              text: 'description',
              isCorrect: false,
              explanation: 'Неправильно. description содержит описание репозитория для GitWeb.',
            },
          ],
          explanation:
            'HEAD — это файл, который указывает на текущую ветку. Его содержимое обычно выглядит как "ref: refs/heads/main".',
          difficulty: 'easy',
        },
        {
          id: '1-2-q2',
          question: 'Что означает код "M " (с пробелом после) в выводе git status --short?',
          options: [
            {
              id: 'a',
              text: 'Файл изменён в рабочей директории, но не в индексе',
              isCorrect: false,
              explanation: 'Неправильно. Это код " M" (пробел перед M).',
            },
            {
              id: 'b',
              text: 'Файл изменён и добавлен в индекс',
              isCorrect: true,
              explanation:
                'Правильно! "M " означает, что файл изменён и находится в индексе (staged).',
            },
            {
              id: 'c',
              text: 'Файл удалён',
              isCorrect: false,
              explanation: 'Неправильно. Удаление обозначается кодом "D".',
            },
            {
              id: 'd',
              text: 'Файл переименован',
              isCorrect: false,
              explanation: 'Неправильно. Переименование обозначается кодом "R".',
            },
          ],
          explanation:
            'В git status --short код "M " (M с пробелом после) означает, что файл изменён и добавлен в индекс. Код " M" (пробел перед M) означает, что файл изменён в рабочей директории, но не добавлен в индекс.',
          difficulty: 'medium',
        },
        {
          id: '1-2-q3',
          question: 'Для чего используется bare-репозиторий?',
          options: [
            {
              id: 'a',
              text: 'Для локальной разработки',
              isCorrect: false,
              explanation:
                'Неправильно. Bare-репозиторий не имеет рабочей директории, поэтому не подходит для разработки.',
            },
            {
              id: 'b',
              text: 'Для серверных репозиториев и CI/CD',
              isCorrect: true,
              explanation:
                'Правильно! Bare-репозитории используются на серверах (GitHub, GitLab) и в CI/CD системах.',
            },
            {
              id: 'c',
              text: 'Для хранения больших файлов',
              isCorrect: false,
              explanation: 'Неправильно. Для больших файлов используется Git LFS.',
            },
            {
              id: 'd',
              text: 'Для шифрования данных',
              isCorrect: false,
              explanation: 'Неправильно. Bare-репозиторий не связан с шифрованием.',
            },
          ],
          explanation:
            'Bare-репозиторий — это репозиторий без рабочей директории. Он используется на серверах (GitHub, GitLab, Bitbucket) и в CI/CD системах, где нужна только история коммитов без файлов.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git status',
          url: 'https://git-scm.com/docs/git-status',
          type: 'documentation',
          description: 'Официальная документация о команде status',
        },
        {
          title: 'Understanding Git Status Output',
          url: 'https://www.atlassian.com/git/tutorials/inspecting-a-repository',
          type: 'article',
          description: 'Статья Atlassian о проверке состояния репозитория',
        },
      ],
    },
    {
      id: '1-3',
      moduleId: 'module-1',
      title: 'Три дерева Git',
      slug: 'the-three-trees',
      order: 3,
      theory: `# Три дерева Git

Git управляет тремя "деревьями", которые представляют различные состояния вашего проекта.

:::diagram three-trees:::

## История концепции

Концепция "трёх деревьев" была формализована в Git для решения фундаментальной проблемы: как дать разработчику контроль над тем, что попадёт в коммит?

### Эволюция подходов:
1. **RCS, CVS** — нет staging area, коммитятся все изменённые файлы
2. **Subversion** — аналогично, нет промежуточного слоя
3. **Git** — ввёл индекс (staging area) для точного контроля

### Почему именно три дерева?
- **Working Directory** — где вы работаете (реальные файлы)
- **Staging Area** — что вы хотите сохранить (контроль)
- **Repository** — что сохранено (история)

## 1. Рабочая директория (Working Directory)

Это ваша фактическая файловая система. Когда вы редактируете файл, изменение существует только в рабочей директории.

- Файлы, которые вы видите в редакторе
- Неотслеживаемые файлы (новые файлы, о которых Git не знает)
- Изменённые файлы (изменены, но не в индексе)

### Глубокое погружение: Состояния файлов

Git отслеживает четыре состояния файла:

\`\`\`
                 ┌─────────────────────────────────────────┐
                 │           Рабочая директория            │
                 │                                         │
   Untracked ────┤    Unmodified ──── Modified ──── Staged │
       │          │         │              │           │   │
       │          │         │              │           │   │
       └──────────┼─────────┼──────────────┼───────────┘   │
                  │         │              │               │
            git add      edit file    git add         git commit
                  │         │              │               │
                  ▼         ▼              ▼               ▼
               Staged   Modified      Staged        Unmodified
\`\`\`

### Состояния файлов:

| Состояние | Описание | Команда для перехода |
|-----------|----------|---------------------|
| **Untracked** | Новый файл, Git не знает о нём | \`git add\` → Staged |
| **Unmodified** | Файл не изменён с последнего коммита | edit → Modified |
| **Modified** | Файл изменён, но не добавлен | \`git add\` → Staged |
| **Staged** | Файл готов к коммиту | \`git commit\` → Unmodified |

## 2. Индекс (Staging Area)

Индекс — это место, где вы подготавливаете изменения перед коммитом.

- Используйте \`git add\` для добавления изменений в индекс
- Содержит изменения, которые будут в следующем коммите
- Думайте об этом как о "превью" вашего коммита

### Глубокое погружение: Внутреннее устройство индекса

Индекс хранится в файле \`.git/index\` — это бинарный файл, содержащий:

\`\`\`
- Заголовок (версия, количество записей)
- Записи для каждого файла:
  - Время изменения (ctime, mtime)
  - Режим файла (permissions)
  - Имя файла
  - SHA-1 хеш blob-объекта
  - Флаги
- Контрольная сумма SHA-1
\`\`\`

### Просмотр содержимого индекса:

\`\`\`bash
# Посмотреть файлы в индексе
git ls-files --stage

# Пример вывода:
# 100644 a1b2c3d... 0    README.md
# 100644 e4f5g6h... 0    src/index.ts
\`\`\`

### Почему индекс важен?

1. **Выборочные коммиты** — можно добавить только часть изменений
2. **Проверка перед коммитом** — \`git diff --staged\` показывает, что попадёт
3. **Разделение изменений** — разные изменения в разных коммитах

## 3. Репозиторий

Репозиторий содержит все ваши закоммиченные изменения.

- Постоянная история вашего проекта
- Каждый коммит — это снимок
- Используйте \`git commit\` для сохранения изменений из индекса

### Глубокое погружение: Как хранятся коммиты

\`\`\`
.git/objects/
├── a1/          # Первые 2 символа SHA-1
│   └── b2c3d... # Остальные 38 символов
├── e4/
│   └── f5g6h...
└── pack/        # Упакованные объекты
\`\`\`

Каждый коммит содержит:
- **Tree** — снимок всех файлов
- **Parent** — ссылка на предыдущий коммит
- **Author** — кто сделал изменение
- **Committer** — кто применил коммит
- **Message** — описание

## Перемещение изменений между деревьями

\`\`\`
Рабочая директория → git add → Индекс → git commit → Репозиторий
\`\`\`

### Все команды перемещения:

| Команда | Откуда | Куда | Описание |
|---------|--------|------|----------|
| \`git add\` | Working Dir | Index | Добавить изменения |
| \`git commit\` | Index | Repository | Сохранить изменения |
| \`git reset --soft\` | Repository | Index | Отменить коммит, сохранив в индексе |
| \`git reset --mixed\` | Repository | Working Dir | Отменить коммит и индекс |
| \`git reset --hard\` | Repository | — | Удалить всё |
| \`git restore --staged\` | Index | Working Dir | Убрать из индекса |
| \`git restore\` | Repository | Working Dir | Отменить изменения |
| \`git checkout\` | Repository | Working Dir | Переключить ветку |

## Практические кейсы

### Кейс 1: "Хочу закоммитить только часть изменений"

\`\`\`bash
# Изменили 3 файла, но хотим закоммитить только 1
git add file1.txt          # Добавить конкретный файл
git commit -m "Исправить file1"

# Или интерактивно выбрать строки
git add -p file2.txt       # Выбрать какие строки добавить
\`\`\`

### Кейс 2: "Случайно добавил файл в индекс"

\`\`\`bash
# Убрать из индекса, сохранив изменения
git restore --staged unwanted-file.txt

# Старый синтаксис (тоже работает)
git reset HEAD unwanted-file.txt
\`\`\`

### Кейс 3: "Хочу отменить изменения в файле"

\`\`\`bash
# Отменить изменения в рабочей директории
git restore filename.txt

# Старый синтаксис
git checkout -- filename.txt
\`\`\`

### Кейс 4: "Хочу понять, что будет закоммичено"

\`\`\`bash
# Изменения в индексе (будут закоммичены)
git diff --staged

# Изменения в рабочей директории (не будут закоммичены)
git diff

# Все незафиксированные изменения
git diff HEAD
\`\`\`

## Типичные ошибки

### Ошибка: "Закоммитил не то"

\`\`\`bash
# Последний коммит содержит лишнее
git reset --soft HEAD~1    # Отменить коммит, сохранив в индексе
git restore --staged unwanted-file.txt  # Убрать лишнее
git commit -m "Правильное сообщение"
\`\`\`

### Ошибка: "Потерял изменения при reset"

\`\`\`bash
# git reset --hard удалил изменения
# Но они могут быть в reflog!
git reflog                # Найти коммит до reset
git checkout <хеш>        # Восстановить
\`\`\`

## Связь с другими концепциями

- **Ветвление** — каждая ветка имеет свой HEAD в репозитории
- **Merge** — объединяет деревья из разных веток
- **Rebase** — переписывает историю в репозитории
- **Stash** — временное хранилище между деревьями
`,
      steps: [
        {
          id: 1,
          title: 'Проверка состояния',
          description: 'Сначала давайте посмотрим текущее состояние нашего репозитория.',
          expectedCommand: 'git status',
          hint: {
            level1: 'Проверьте, какие файлы существуют и их статус.',
            level2: 'Используйте git status.',
            level3: 'Введите: git status',
          },
        },
      ],
      keyInsight: {
        main: 'Понимание трёх деревьев необходимо — все команды Git перемещают изменения между этими деревьями.',
        example:
          'Когда вы редактируете файл — он в Working Directory. git add перемещает его в Staging Area. git commit сохраняет в Repository. git reset --soft возвращает из Repository в Staging. git reset --mixed возвращает в Working Directory.',
        warning:
          'Не путайте Staging Area с репозиторием! Изменения в индексе ещё не сохранены — они пропадут при сбое. Всегда делайте commit после add.',
        tip: 'Используйте git diff для просмотра изменений в Working Directory, git diff --staged для изменений в Staging Area. Это поможет понять, что именно будет закоммичено.',
        additional: [
          'Working Directory — это ваши файлы на диске, которые вы редактируете.',
          'Staging Area (индекс) — это "черновик" следующего коммита.',
          'Repository — это база данных всех коммитов в .git директории.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '1-3-q1',
          question: 'Какие три "дерева" управляет Git?',
          options: [
            {
              id: 'a',
              text: 'Working Directory, Staging Area, Repository',
              isCorrect: true,
              explanation:
                'Правильно! Git управляет тремя деревьями: рабочая директория, индекс (staging area) и репозиторий.',
            },
            {
              id: 'b',
              text: 'Local, Remote, Cloud',
              isCorrect: false,
              explanation: 'Неправильно. Это уровни хранения, а не деревья Git.',
            },
            {
              id: 'c',
              text: 'Branch, Tag, Commit',
              isCorrect: false,
              explanation: 'Неправильно. Это типы ссылок в Git.',
            },
            {
              id: 'd',
              text: 'Source, Build, Deploy',
              isCorrect: false,
              explanation: 'Неправильно. Это этапы CI/CD пайплайна.',
            },
          ],
          explanation:
            'Git управляет тремя деревьями: Working Directory (файлы на диске), Staging Area/Index (подготовленные изменения) и Repository (история коммитов).',
          difficulty: 'easy',
        },
        {
          id: '1-3-q2',
          question: 'В каком состоянии файл, если он "Staged"?',
          options: [
            {
              id: 'a',
              text: 'Файл изменён в рабочей директории',
              isCorrect: false,
              explanation: 'Неправильно. Это состояние "Modified".',
            },
            {
              id: 'b',
              text: 'Файл готов к коммиту',
              isCorrect: true,
              explanation:
                'Правильно! Staged означает, что файл добавлен в индекс и готов к коммиту.',
            },
            {
              id: 'c',
              text: 'Файл не отслеживается Git',
              isCorrect: false,
              explanation: 'Неправильно. Это состояние "Untracked".',
            },
            {
              id: 'd',
              text: 'Файл удалён',
              isCorrect: false,
              explanation: 'Неправильно. Удаление — это отдельное состояние.',
            },
          ],
          explanation:
            'Staged (в индексе) означает, что файл был добавлен командой git add и готов к коммиту. После коммита файл переходит в состояние Unmodified.',
          difficulty: 'easy',
        },
        {
          id: '1-3-q3',
          question: 'Какая команда перемещает изменения из Repository в Index?',
          options: [
            {
              id: 'a',
              text: 'git add',
              isCorrect: false,
              explanation: 'Неправильно. git add перемещает из Working Directory в Index.',
            },
            {
              id: 'b',
              text: 'git reset --soft',
              isCorrect: true,
              explanation:
                'Правильно! git reset --soft отменяет коммит, сохраняя изменения в индексе.',
            },
            {
              id: 'c',
              text: 'git commit',
              isCorrect: false,
              explanation: 'Неправильно. git commit перемещает из Index в Repository.',
            },
            {
              id: 'd',
              text: 'git restore',
              isCorrect: false,
              explanation: 'Неправильно. git restore перемещает из Repository в Working Directory.',
            },
          ],
          explanation:
            'git reset --soft перемещает HEAD назад, но сохраняет изменения в индексе. Это полезно для пересоздания коммита с другими изменениями.',
          difficulty: 'medium',
        },
        {
          id: '1-3-q4',
          question: 'Где хранится индекс (Staging Area)?',
          options: [
            {
              id: 'a',
              text: '.git/index',
              isCorrect: true,
              explanation: 'Правильно! Индекс хранится в бинарном файле .git/index.',
            },
            {
              id: 'b',
              text: '.git/objects/',
              isCorrect: false,
              explanation: 'Неправильно. В objects хранятся коммиты, деревья и blob-объекты.',
            },
            {
              id: 'c',
              text: '.git/refs/',
              isCorrect: false,
              explanation: 'Неправильно. В refs хранятся ссылки на ветки и теги.',
            },
            {
              id: 'd',
              text: '.git/HEAD',
              isCorrect: false,
              explanation: 'Неправильно. HEAD указывает на текущую ветку.',
            },
          ],
          explanation:
            'Индекс хранится в бинарном файле .git/index. Он содержит список файлов, их режимы, хеши и метаданные для следующего коммита.',
          difficulty: 'medium',
        },
      ],
      resources: [
        {
          title: 'Git Internals: The Three Trees',
          url: 'https://git-scm.com/book/en/v2/Git-Internals-Git-Objects',
          type: 'book',
          description: 'Глава Pro Git о внутренней структуре Git',
        },
        {
          title: 'Working Directory, Staging Area, and Repository',
          url: 'https://medium.com/@lucasmoyer/git-staging-area-explained-da3471825a22',
          type: 'article',
          description: 'Статья о трёх деревьях Git с наглядными схемами',
        },
      ],
    },
    {
      id: '1-4',
      moduleId: 'module-1',
      title: 'Индекс: глубокое погружение',
      slug: 'staging-area-deep-dive',
      order: 4,
      theory: `# Индекс: глубокое погружение

Индекс (также называемый "staging area") — одна из самых мощных возможностей Git.

:::diagram staging-flow:::

## История появления индекса

Индекс — это уникальная особенность Git, которой не было в предшественниках.

### Эволюция подходов к коммиту:

| Система | Подход | Проблема |
|---------|--------|----------|
| RCS, CVS | Коммитятся все изменённые файлы | Нет контроля |
| Subversion | Аналогично | Нельзя разделить изменения |
| **Git** | **Индекс (staging area)** | **Полный контроль** |

### Почему индекс важен?

Представьте: вы работали над функцией, но параллельно:
- Добавили отладочные \`console.log\`
- Исправили опечатку в другом файле
- Добавили временный файл с тестовыми данными

Без индекса всё это попало бы в коммит. С индексом вы выбираете только нужное.

## Зачем добавлять изменения в индекс?

1. **Выборочные коммиты**: Выбирайте именно те изменения, которые включить
2. **Проверка перед коммитом**: Смотрите, что будет закоммичено
3. **Разделение изменений**: Делайте несколько сфокусированных коммитов из одного набора изменений

## Глубокое погружение: Внутреннее устройство индекса

### Файл .git/index

Индекс хранится в бинарном файле \`.git/index\`. Это не просто список файлов!

\`\`\`
Структура файла index:
┌─────────────────────────────────────┐
│ Header (12 байт)                    │
│ - Сигнатура: "DIRC"                 │
│ - Версия формата                    │
│ - Количество записей                │
├─────────────────────────────────────┤
│ Index Entries (по одной на файл)    │
│ - ctime: время создания файла       │
│ - mtime: время модификации          │
│ - dev: устройство                   │
│ - ino: inode                        │
│ - mode: права доступа               │
│ - uid, gid: владелец и группа       │
│ - file size                         │
│ - SHA-1: хеш содержимого            │
│ - flags: флаги                      │
│ - name: имя файла                   │
├─────────────────────────────────────┤
│ SHA-1 Checksum (20 байт)            │
└─────────────────────────────────────┘
\`\`\`

### Просмотр содержимого индекса

\`\`\`bash
# Посмотреть файлы в индексе с их режимами и хешами
git ls-files --stage

# Пример вывода:
# 100644 a1b2c3d4e5f6... 0    README.md
# 100755 e4f5g6h7i8j9... 0    scripts/build.sh
# 100644 i7j8k9l0m1n2... 0    src/index.ts
\`\`\`

### Режимы файлов в индексе

| Режим | Значение |
|-------|----------|
| 100644 | Обычный файл |
| 100755 | Исполняемый файл |
| 120000 | Символическая ссылка |

## Добавление файлов

### Базовые команды

\`\`\`bash
# Добавить конкретный файл
git add filename.txt

# Добавить все изменения в текущей директории
git add .

# Добавить все изменения в репозитории
git add -A
\`\`\`

### Продвинутые опции

\`\`\`bash
# Интерактивное добавление (выбор строк)
git add -p filename.txt

# Интерактивный режим (меню)
git add -i

# Добавить с подтверждением каждого файла
git add -A --dry-run && git add -A

# Добавить только отслеживаемые файлы
git add -u
\`\`\`

### Интерактивное добавление (git add -p)

При использовании \`git add -p\` Git показывает каждый "hunk" (блок изменений):

\`\`\`bash
$ git add -p src/auth.ts
diff --git a/src/auth.ts b/src/auth.ts
@@ -10,6 +10,8 @@ function login(user) {
   const token = generateToken(user);
+  console.log('Debug: token', token);  // Отладка
   saveSession(token);
+  // TODO: добавить валидацию
   return token;
 }
(1/1) Stage this hunk [y,n,q,a,d,e,?]?
\`\`\`

**Опции:**
- \`y\` — добавить этот блок
- \`n\` — не добавлять
- \`q\` — выйти
- \`a\` — добавить этот и все последующие
- \`d\` — не добавлять этот и все последующие
- \`e\` — редактировать блок вручную
- \`s\` — разбить на меньшие блоки

## Удаление из индекса

\`\`\`bash
# Убрать файл из индекса (изменения останутся в рабочей директории)
git restore --staged filename.txt

# Или используйте старый синтаксис
git reset HEAD filename.txt

# Убрать все файлы из индекса
git restore --staged .
# или
git reset HEAD
\`\`\`

### Разница между restore и reset

| Команда | Действие |
|---------|----------|
| \`git restore --staged file\` | Убирает из индекса, изменения остаются |
| \`git restore file\` | Отменяет изменения в рабочей директории |
| \`git reset HEAD file\` | Аналог restore --staged |
| \`git checkout -- file\` | Аналог restore (старый синтаксис) |

## Просмотр изменений в индексе

\`\`\`bash
# Посмотреть, что в индексе (будет закоммичено)
git diff --staged

# Или (синоним)
git diff --cached

# Посмотреть изменения в рабочей директории (не будут закоммичены)
git diff

# Посмотреть все незафиксированные изменения
git diff HEAD
\`\`\`

### Пример вывода git diff --staged

\`\`\`bash
$ git diff --staged
diff --git a/README.md b/README.md
index a1b2c3d..e4f5g6h 100644
--- a/README.md
+++ b/README.md
@@ -1,3 +1,5 @@
 # My Project
 
+Описание проекта...
+
 ## Installation
\`\`\`

## Практические кейсы

### Кейс 1: "Разделение изменений на несколько коммитов"

\`\`\`bash
# Изменили 3 файла: багфикс, новая функция, документация
git status
# modified: src/auth.ts      (багфикс)
# modified: src/feature.ts   (новая функция)
# modified: README.md        (документация)

# Коммит 1: Багфикс
git add src/auth.ts
git commit -m "Исправить ошибку валидации"

# Коммит 2: Новая функция
git add src/feature.ts
git commit -m "Добавить функцию экспорта"

# Коммит 3: Документация
git add README.md
git commit -m "Обновить документацию"
\`\`\`

### Кейс 2: "Частичное добавление изменений в файле"

\`\`\`bash
# В одном файле несколько изменений
git add -p src/api.ts

# Git покажет каждое изменение отдельно
# Выберите какие добавить, какие пропустить
\`\`\`

### Кейс 3: "Случайно добавили лишнее в индекс"

\`\`\`bash
# Добавили всё подряд
git add .

# Ой, не надо было добавлять config.local
git restore --staged config.local

# Проверим, что осталось в индексе
git diff --staged
\`\`\`

### Кейс 4: "Отмена изменений в рабочей директории"

\`\`\`bash
# Изменили файл, но поняли, что не нужно
git restore filename.txt

# Файл вернулся к состоянию последнего коммита
\`\`\`

## Типичные ошибки

### Ошибка: "Закоммитил лишнее"

\`\`\`bash
# Последний коммит содержит файл, который не должен был попасть
git reset --soft HEAD~1          # Отменить коммит, сохранив в индексе
git restore --staged unwanted.txt # Убрать лишнее
git commit -m "Правильное сообщение"
\`\`\`

### Ошибка: "Потерял изменения при git restore"

\`\`\`bash
# git restore отменил изменения безвозвратно!
# Но если вы делали git stash раньше...
git stash list
git stash pop
\`\`\`

### Ошибка: "Конфликт между индексом и рабочей директорией"

\`\`\`bash
# Файл в индексе, но изменён в рабочей директории
git status
# Changes to be committed:
#   modified: file.txt
# Changes not staged for commit:
#   modified: file.txt

# Посмотреть разницу между индексом и рабочей директорией
git diff file.txt

# Посмотреть разницу между индексом и последним коммитом
git diff --staged file.txt
\`\`\`

## Связь с другими концепциями

- **git stash** — сохраняет изменения из рабочей директории и индекса
- **git reset** — управляет тем, что происходит с индексом
- **git checkout** — обновляет индекс при переключении веток
- **.gitignore** — файлы в игноре не попадают в индекс
`,
      steps: [
        {
          id: 1,
          title: 'Добавление файла в индекс',
          description: 'Добавьте файл с именем "README.md" в индекс.',
          expectedCommand: 'git add README.md',
          validationRegex: 'git add\\s+.*',
          hint: {
            level1: 'Вам нужно добавить файл в индекс для следующего коммита.',
            level2: 'Используйте git add с именем файла.',
            level3: 'Введите: git add README.md',
          },
        },
        {
          id: 2,
          title: 'Проверка индексированных изменений',
          description: 'Убедитесь, что файл добавлен в индекс, проверив статус.',
          expectedCommand: 'git status',
          hint: {
            level1: 'Проверьте, какие файлы в индексе.',
            level2: 'Используйте git status.',
            level3: 'Введите: git status',
          },
        },
      ],
      keyInsight: {
        main: 'Индекс позволяет создавать чистые, сфокусированные коммиты, выбирая именно те изменения, которые нужно включить.',
        example:
          'Вы изменили 3 файла: исправили баг в auth.ts, добавили логирование в logger.ts, обновили README.md. Можно добавить только auth.ts и закоммитить как "Исправить баг аутентификации", а logger.ts закоммитить отдельно как "Добавить логирование".',
        warning:
          'git add . добавляет все изменения в текущей директории. Будьте осторожны — можете случайно добавить ненужные файлы. Лучше использовать git add -p для интерактивного выбора изменений.',
        tip: 'Используйте git add -p (patch mode) для выбора конкретных строк из файла. Это позволяет разделить изменения в одном файле на разные коммиты.',
        additional: [
          'git restore --staged <file> убирает файл из индекса, не удаляя изменения.',
          'git diff --staged показывает, что попадёт в коммит.',
          'Интерактивный режим git add -i даёт больше контроля.',
        ],
      },
      estimatedTime: 15,
      quizQuestions: [
        {
          id: '1-4-q1',
          question: 'Какая команда позволяет интерактивно выбирать строки для добавления в индекс?',
          options: [
            {
              id: 'a',
              text: 'git add -i',
              isCorrect: false,
              explanation:
                'Неправильно. git add -i открывает интерактивное меню, но не выбор строк.',
            },
            {
              id: 'b',
              text: 'git add -p',
              isCorrect: true,
              explanation:
                'Правильно! git add -p (patch) позволяет выбирать конкретные блоки изменений для добавления.',
            },
            {
              id: 'c',
              text: 'git add -A',
              isCorrect: false,
              explanation: 'Неправильно. git add -A добавляет все изменения без выбора.',
            },
            {
              id: 'd',
              text: 'git add -u',
              isCorrect: false,
              explanation: 'Неправильно. git add -u добавляет только отслеживаемые файлы.',
            },
          ],
          explanation:
            'git add -p (patch mode) позволяет просматривать каждый блок изменений (hunk) и решать, добавлять его в индекс или нет. Это полезно для разделения изменений в одном файле на разные коммиты.',
          difficulty: 'medium',
        },
        {
          id: '1-4-q2',
          question:
            'Какая команда убирает файл из индекса, сохраняя изменения в рабочей директории?',
          options: [
            {
              id: 'a',
              text: 'git rm filename',
              isCorrect: false,
              explanation: 'Неправильно. git rm удаляет файл из индекса И рабочей директории.',
            },
            {
              id: 'b',
              text: 'git restore --staged filename',
              isCorrect: true,
              explanation:
                'Правильно! git restore --staged убирает файл из индекса, сохраняя изменения.',
            },
            {
              id: 'c',
              text: 'git reset --hard',
              isCorrect: false,
              explanation: 'Неправильно. git reset --hard удаляет все изменения!',
            },
            {
              id: 'd',
              text: 'git checkout -- filename',
              isCorrect: false,
              explanation: 'Неправильно. Эта команда отменяет изменения в рабочей директории.',
            },
          ],
          explanation:
            'git restore --staged (или git reset HEAD) убирает файл из индекса, но сохраняет изменения в рабочей директории. Это полезно, если вы случайно добавили файл в индекс.',
          difficulty: 'easy',
        },
        {
          id: '1-4-q3',
          question: 'Что показывает команда git diff --staged?',
          options: [
            {
              id: 'a',
              text: 'Изменения в рабочей директории',
              isCorrect: false,
              explanation: 'Неправильно. Для этого используйте git diff без флагов.',
            },
            {
              id: 'b',
              text: 'Изменения в индексе (готовые к коммиту)',
              isCorrect: true,
              explanation:
                'Правильно! git diff --staged показывает разницу между индексом и последним коммитом.',
            },
            {
              id: 'c',
              text: 'Все незафиксированные изменения',
              isCorrect: false,
              explanation: 'Неправильно. Для этого используйте git diff HEAD.',
            },
            {
              id: 'd',
              text: 'Разницу между ветками',
              isCorrect: false,
              explanation: 'Неправильно. Для этого укажите ветки: git diff branch1..branch2.',
            },
          ],
          explanation:
            'git diff --staged (или git diff --cached) показывает изменения, которые были добавлены в индекс и будут закоммичены. Это полезно для проверки перед коммитом.',
          difficulty: 'easy',
        },
        {
          id: '1-4-q4',
          question: 'Какой режим файла в индексе означает, что файл исполняемый?',
          options: [
            {
              id: 'a',
              text: '100644',
              isCorrect: false,
              explanation: 'Неправильно. 100644 — это обычный файл (не исполняемый).',
            },
            {
              id: 'b',
              text: '100755',
              isCorrect: true,
              explanation: 'Правильно! 100755 — это исполняемый файл (например, скрипты).',
            },
            {
              id: 'c',
              text: '120000',
              isCorrect: false,
              explanation: 'Неправильно. 120000 — это символическая ссылка.',
            },
            {
              id: 'd',
              text: '040000',
              isCorrect: false,
              explanation: 'Неправильно. 040000 — это директория (tree object).',
            },
          ],
          explanation:
            'В индексе Git файлы имеют режимы: 100644 — обычный файл, 100755 — исполняемый файл, 120000 — символическая ссылка. Это соответствует Unix-правам доступа.',
          difficulty: 'hard',
        },
      ],
      resources: [
        {
          title: 'Git Documentation: git add',
          url: 'https://git-scm.com/docs/git-add',
          type: 'documentation',
          description: 'Официальная документация о команде add',
        },
        {
          title: 'Interactive Staging',
          url: 'https://git-scm.com/book/en/v2/Git-Tools-Interactive-Staging',
          type: 'book',
          description: 'Глава Pro Git об интерактивном добавлении файлов',
        },
        {
          title: 'Git Add Interactive Mode',
          url: 'https://www.youtube.com/watch?v=7G2r5XKwmAo',
          type: 'video',
          description: 'Видео о интерактивном режиме git add',
        },
      ],
    },
  ],
};

export { module1 };
